import { PaymentMethod } from 'types';
import type {
  GooglePayToken,
  MakePaymentV3Params,
} from 'services/repositories/payment/makePaymentV3';
import { generateUniqueId } from './generateUniqueId';

type PaymentMethodPayload = MakePaymentV3Params['paymentMethods'][number];

interface BuildGooglePayPaymentMethodParams {
  /** Opaque token from paymentData.paymentMethodData.tokenizationData.token. */
  token: GooglePayToken;
  /** Amount to charge, in cents. */
  amountInCents: number;
  /** Installments (captured before the button). Default 1 (one-off). */
  installments?: number;
}

/**
 * Builds the Google Pay payment item for `POST /v3/payment`.
 *
 * Like Apple Pay, the token comes from the wallet callback (loadPaymentData),
 * not from formik, so the item is built here — not inside `buildPaymentMethods`.
 * BFF contract: the agnostic wallet pair `walletToken: { wallet, token }`, so a
 * future wallet needs no new field anywhere in the chain. The backend forwards
 * the token whole to the PGA and decrypts it (ECv2, DIRECT) in acquirer-api;
 * there is no `validate-merchant` step. See FIN-7445 / FIN-7487.
 */
export function buildGooglePayPaymentMethod({
  token,
  amountInCents,
  installments = 1,
}: BuildGooglePayPaymentMethodParams): PaymentMethodPayload {
  return {
    virtualId: generateUniqueId(),
    type: PaymentMethod.GOOGLE_PAY,
    amount: amountInCents,
    installments,
    walletToken: { wallet: 'GOOGLE_PAY', token },
  };
}
